<template>
  <InputField label="Expiration MM/YY" :error="hasError" :error-message="validationMessage" :active="isFocused" :filled="!isEmpty">
    <div class="arcade-checkout-payment-credit-card-date">
      <div id="recurly-card-input-field-month"></div>
      <div id="recurly-card-input-field-slash">/</div>
      <div id="recurly-card-input-field-year"></div>
    </div>
  </InputField>
</template>
<script setup lang="ts">
import InputField from '@/components/shared/inputs/InputField.vue'

import { toRaw, ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useErrorsStore } from '@/store/ErrorsStore'
import { useRecurlyStore } from '@/store/RecurlyStore'

const recurlyStore = useRecurlyStore()
const { recurlyElements } = storeToRefs(recurlyStore)

const props = defineProps({
  styles: {
    type: Object,
    required: true
  }
})

const errorsStore = useErrorsStore()
const { find: findError } = errorsStore
const { errorType } = storeToRefs(errorsStore)

const attached = ref(false)
const isMonthValid = ref(false)
const isMonthEmpty = ref(true)
const isYearValid = ref(false)
const isYearEmpty = ref(true)
const isFocused = ref(false)

const isValid = computed(() => isMonthValid.value && isYearValid.value)
const isEmpty = computed(() => isMonthEmpty.value && isYearEmpty.value)

defineExpose({ isValid })

const isDateError = computed(() => {
  return findError('month') || findError('year')
})

const transactionError = computed(() => {
  return errorType.value === 'transaction'
})

const validationError = computed(() => {
  return (isDateError.value && !isValid.value) || (!isValid.value && !isEmpty.value)
})

const hasError = computed(() => {
  return validationError.value || transactionError.value
})

const validationMessage = computed(() => {
  return validationError.value ? 'Your expiration date is not valid' : undefined
})

onMounted(() => {
  attachRecurly()
})

const attachRecurly = () => {
  if (attached.value || !recurlyElements.value) {
    return false
  }

  attachMonth()
  attachYear()
  attached.value = true
}

const attachMonth = () => {
  const monthElement = toRaw(recurlyElements.value)
    .CardMonthElement({
      style: {
        ...props.styles,
        placeholder: {
          content: 'MM',
          fontWeight: 'normal',
          color: (props.styles.placeholderColor || '#FFFFFF') + ' !important'
        }
      },
      inputType: 'mobileSelect'
    })
    .attach('#recurly-card-input-field-month')

  monthElement.on('change', ({ empty, valid }: { empty: boolean; valid: boolean }) => {
    isMonthEmpty.value = empty
    isMonthValid.value = valid
  })

  monthElement.on('focus', () => {
    isFocused.value = true
  })

  monthElement.on('blur', () => {
    isFocused.value = false
  })
}

const attachYear = () => {
  const yearElement = toRaw(recurlyElements.value)
    .CardYearElement({
      style: {
        ...props.styles,
        placeholder: {
          content: 'YY',
          fontWeight: 'normal',
          color: (props.styles.placeholderColor || '#FFFFFF') + ' !important'
        }
      },
      inputType: 'mobileSelect'
    })
    .attach('#recurly-card-input-field-year')

  yearElement.on('change', ({ empty, valid }: { empty: boolean; valid: boolean }) => {
    isYearEmpty.value = empty
    isYearValid.value = valid
  })

  yearElement.on('focus', () => {
    isFocused.value = true
  })

  yearElement.on('blur', () => {
    isFocused.value = false
  })
}
</script>
<style lang="scss" scoped>
.arcade-checkout-payment-credit-card-date {
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  width: 100%;
  max-height: 48px;
  flex-shrink: 0;
  #recurly-card-input-field-month {
    width: 40px;

    @include media-breakpoint(sm) {
      width: 30px;
    }
  }
  #recurly-card-input-field-slash {
    width: 12px;
    text-align: center;
  }
  #recurly-card-input-field-year {
    width: 64px;
  }
}
</style>
